import { Component, OnDestroy, OnInit, ViewChild, signal } from '@angular/core';
import { Subject, forkJoin, fromEvent, of } from 'rxjs';
import { catchError, map, takeUntil } from 'rxjs/operators';
import { AdminConfigService } from '../../services/admin-config.service';
import { ListDataService } from '../../services/list-data.service';
import { ToastNotificationComponent } from '../../toast-notification/toast-notification.component';
import { ListItem } from '../../models/ListItem';
import { LocationListItem } from '../../models/LocationListItem';
import { LocationSearchParameters } from '../../models/LocationSearchParameters';
import { AcsTradeUnitDefault } from '../models/acs.models';
import { ACS_TOAST_SUCCESS_DURATION, ACS_TOAST_ERROR_DURATION } from '../utils/acs.constants';
import { extractErrorMessage, resolveLocCode, scopedLocsToListItems } from '../utils/acs.utils';
import { AcsDropdownState } from '../utils/acs-dropdown-state';
import { AcsDropdownService } from '../utils/acs-dropdown.service';

interface CompuweighEntry {
  locDefaultId: number;
  key: string;
  value: string;
  locationCode: string;
  locationName: string;
  city: string;
}

@Component({
  selector: 'app-acs-trade-unit-default',
  templateUrl: './acs-trade-unit-default.component.html',
  styleUrls: ['../acs-shared.css', './acs-trade-unit-default.component.css'],
  standalone: false
})
export class AcsTradeUnitDefaultComponent implements OnInit, OnDestroy {

  private _destroy$: Subject<void> = new Subject<void>();

  @ViewChild('toastNotification') toastNotification!: ToastNotificationComponent;

  locList: ListItem[] = [];
  locDropdown!: AcsDropdownState;

  form: { [key: string]: string } = {};
  initialValues: { [key: string]: string } = {};
  keyIdMap: { [key: string]: number } = {};
  formAccountDefault: string = '';
  initialAccountDefault: string = '';
  isDirty: boolean = false;
  isSaving: boolean = false;

  // CompuWeigh Integration
  compuweighEntries: CompuweighEntry[] = [];
  compuweighLoading = false;
  cwNewType = '';
  cwNewLocationId: number | null = null;
  cwNewLocationDisplay = '';
  cwLocationResults: LocationListItem[] = [];
  cwLocationSearchText = signal('');
  cwIsSearching = false;
  cwIsSaving = false;

  defaultKeyGroups: { group: string; keys: { key: string; type: string; options?: string[] }[] }[] = [
    {
      group: 'Confirmation Disclaimer',
      keys: [
        { key: 'ConfirmationDisclaimer', type: 'text' }
      ]
    },
    {
      group: 'Schedule',
      keys: [
        { key: 'DefaultPlanningWindow', type: 'dropdown', options: ['Today', 'Tomorrow', 'Yesterday', 'Current Week', 'Previous Week', 'Next Week', 'Current Month', 'Previous Month', 'Next Month', 'Custom'] },
        { key: 'DefaultPlanningPerspective', type: 'dropdown', options: ['Destination', 'Origin'] },
        { key: 'ShowSchedulingByDay', type: 'boolean' }
      ]
    },
    {
      group: 'Dispatch',
      keys: [
        { key: 'AccountDefaultToScoular', type: 'radio' },
        { key: 'AccountDefaultToLastAccountUsed', type: 'radio' },
        { key: 'IsOTCEnabled', type: 'boolean' },
        { key: 'OTCNote', type: 'text' },
        { key: 'ShowEquipmentFields', type: 'boolean' }
      ]
    },
    {
      group: 'Origin Confirmation',
      keys: [
        { key: 'OriginConfBreakoutByDay', type: 'boolean' },
        { key: 'OriginConfShowPurchaseContract', type: 'boolean' },
        { key: 'OriginConfShowTrucker', type: 'boolean' },
        { key: 'OriginConfShowDest', type: 'boolean' },
        { key: 'OriginConfShowDestCityState', type: 'boolean' },
        { key: 'OriginConfShowLoadNumbers', type: 'boolean' },
        { key: 'OriginConfShowDriverName', type: 'boolean' },
        { key: 'OriginConfShowPlateNumber', type: 'boolean' }
      ]
    },
    {
      group: 'Destination Confirmation',
      keys: [
        { key: 'DestConfBreakoutByDay', type: 'boolean' },
        { key: 'DestConfShowOrigin', type: 'boolean' },
        { key: 'DestConfShowTrucker', type: 'boolean' },
        { key: 'DestConfUseDeliveryDate', type: 'boolean' },
        { key: 'DestConfShowUnloadNum', type: 'boolean' },
        { key: 'DestConfHideUnassignedTrucker', type: 'boolean' },
        { key: 'DestConfShowOriginCustomer', type: 'boolean' }
      ]
    },
    {
      group: 'Trucker Confirmation',
      keys: [
        { key: 'BreakoutByDayOverride', type: 'boolean' }
      ]
    },
    {
      group: 'Compuweigh',
      keys: [
        { key: 'IsCompuweighEnabled', type: 'boolean' }
      ]
    }
  ];

  allKeys: string[] = this.defaultKeyGroups.flatMap(g => g.keys.map(k => k.key));

  constructor(
    private _adminConfigService: AdminConfigService,
    private _listDataService: ListDataService,
    public dropdowns: AcsDropdownService
  ) {
    this.locDropdown = new AcsDropdownState('tud-loc', this.dropdowns, () => this.locList, '-- Select Trade Unit --');
  }

  ngOnInit(): void {
    this._adminConfigService.getScopedLocs().pipe(takeUntil(this._destroy$)).subscribe({
      next: (result) => this.locList = scopedLocsToListItems(result, true)
    });
    fromEvent(document, 'click').pipe(takeUntil(this._destroy$)).subscribe(() => {
      this.dropdowns.close();
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next();
    this._destroy$.complete();
  }

  selectLocOption(loc: ListItem | null): void {
    this.locDropdown.select(loc);
    this.loadDefaults();
  }

  loadDefaults(): void {
    if (!this.locDropdown.selectedValue) {
      this.resetForm();
      return;
    }
    const locCode: string = this.getEffectiveLocCode();
    this._adminConfigService.getTradeUnitDefaults(locCode, '').pipe(takeUntil(this._destroy$)).subscribe({
      next: (data) => this.parseDefaults(data)
    });
  }

  private parseDefaults(data: AcsTradeUnitDefault[]): void {
    this.form = {};
    this.initialValues = {};
    this.keyIdMap = {};

    for (const key of this.allKeys) {
      this.form[key] = '';
      this.initialValues[key] = '';
    }

    for (const item of data) {
      if (this.allKeys.includes(item.key)) {
        this.form[item.key] = item.value || '';
        this.initialValues[item.key] = item.value || '';
        this.keyIdMap[item.key] = item.locDefaultId;
      }
    }

    const cwKeys = ['CompuweighOriginID', 'CompuweighDestinationID'];
    const rawCw = data.filter(item => cwKeys.includes(item.key));
    this.resolveCompuweighEntries(rawCw);

    this.deriveAccountDefault();
    this.initialAccountDefault = this.formAccountDefault;
    this.isDirty = false;
  }

  private resolveCompuweighEntries(rawEntries: AcsTradeUnitDefault[]): void {
    if (rawEntries.length === 0) {
      this.compuweighEntries = [];
      this.compuweighLoading = false;
      return;
    }

    this.compuweighLoading = true;
    const lookups = rawEntries.map(item =>
      this._listDataService.getLocation(+item.value, this.getEffectiveLocCode()).pipe(
        map((loc: LocationListItem) => ({
          locDefaultId: item.locDefaultId,
          key: item.key,
          value: item.value,
          locationCode: loc.locationCode,
          locationName: loc.locationName,
          city: loc.city
        })),
        catchError(() => of({
          locDefaultId: item.locDefaultId,
          key: item.key,
          value: item.value,
          locationCode: '',
          locationName: `Location #${item.value}`,
          city: ''
        }))
      )
    );

    forkJoin(lookups).pipe(takeUntil(this._destroy$)).subscribe({
      next: (entries) => {
        this.compuweighEntries = entries;
        this.compuweighLoading = false;
      },
      error: () => {
        this.compuweighEntries = [];
        this.compuweighLoading = false;
      }
    });
  }

  private deriveAccountDefault(): void {
    if (this.form['AccountDefaultToScoular'] === '1') {
      this.formAccountDefault = 'scoular';
    } else if (this.form['AccountDefaultToLastAccountUsed'] === '1') {
      this.formAccountDefault = 'lastAccount';
    } else {
      this.formAccountDefault = '';
    }
  }

  private resetForm(): void {
    this.form = {};
    this.initialValues = {};
    this.keyIdMap = {};
    this.formAccountDefault = '';
    this.initialAccountDefault = '';
    this.isDirty = false;
    this.compuweighEntries = [];
    this.resetCwForm();
  }

  isChecked(key: string): boolean {
    return this.form[key] === '1';
  }

  setChecked(key: string, checked: boolean): void {
    this.form[key] = checked ? '1' : '0';
    this.markDirty();
  }

  markDirty(): void {
    this.isDirty = true;
  }

  cancelChanges(): void {
    this.form = { ...this.initialValues };
    this.formAccountDefault = this.initialAccountDefault;
    this.isDirty = false;
  }

  saveAll(): void {
    this.form['AccountDefaultToScoular'] = this.formAccountDefault === 'scoular' ? '1' : '0';
    this.form['AccountDefaultToLastAccountUsed'] = this.formAccountDefault === 'lastAccount' ? '1' : '0';

    const dtos: any[] = [];
    for (const key of this.allKeys) {
      const currentVal = this.form[key] || '';
      const initialVal = this.initialValues[key] || '';
      if (currentVal !== initialVal) {
        dtos.push({
          locDefaultId: this.keyIdMap[key] || 0,
          locCode: this.getEffectiveLocCode(),
          commodityCode: null,
          key: key,
          value: currentVal
        });
      }
    }

    if (dtos.length === 0) {
      this.isDirty = false;
      return;
    }

    this.isSaving = true;
    const saves = dtos.map(d => this._adminConfigService.saveTradeUnitDefault(d));

    forkJoin(saves).pipe(takeUntil(this._destroy$)).subscribe({
      next: () => {
        this.toastNotification.showToast(`Saved ${dtos.length} default(s)`, ACS_TOAST_SUCCESS_DURATION, 'success');
        this.isSaving = false;
        this.loadDefaults();
      },
      error: (err) => {
        const msg: string = extractErrorMessage(err, 'Failed to save defaults');
        this.toastNotification.showToast(msg, ACS_TOAST_ERROR_DURATION, 'error');
        this.isSaving = false;
      }
    });
  }

  private getEffectiveLocCode(): string {
    return resolveLocCode(this.locDropdown.selectedValue, this.locList);
  }

  // --- CompuWeigh Integration ---

  getEntryTypeDisplay(key: string): string {
    return key === 'CompuweighOriginID' ? 'Origin Site' : 'Destination Site';
  }

  getEntryLocationDisplay(entry: CompuweighEntry): string {
    if (entry.locationCode) return entry.locationCode + ' — ' + entry.locationName;
    return entry.locationName;
  }

  toggleCwLocationDropdown(): void {
    this.dropdowns.toggle('tud-cw-location');
    if (this.dropdowns.isOpen('tud-cw-location')) {
      this.cwLocationSearchText.set('');
      this.cwLocationResults = [];
    }
  }

  searchCwLocations(): void {
    const term = this.cwLocationSearchText().trim();
    if (term.length < 2) return;
    this.cwIsSearching = true;
    const params = new LocationSearchParameters();
    params.locationName = term;
    this._listDataService.searchLocations(params).pipe(takeUntil(this._destroy$)).subscribe({
      next: (data) => {
        this.cwLocationResults = data;
        this.cwIsSearching = false;
      },
      error: () => {
        this.cwLocationResults = [];
        this.cwIsSearching = false;
      }
    });
  }

  selectCwLocation(loc: LocationListItem): void {
    this.cwNewLocationId = loc.locationId;
    this.cwNewLocationDisplay = loc.locationCode + ' — ' + loc.locationName
      + (loc.city ? ', ' + loc.city : '');
    this.cwLocationSearchText.set('');
    this.dropdowns.close();
  }

  clearCwLocation(): void {
    this.cwNewLocationId = null;
    this.cwNewLocationDisplay = '';
  }

  get cwIsDuplicate(): boolean {
    if (!this.cwNewType || !this.cwNewLocationId) return false;
    return this.compuweighEntries.some(
      e => e.key === this.cwNewType && e.value === this.cwNewLocationId!.toString()
    );
  }

  get cwCanAdd(): boolean {
    return !!this.cwNewType && !!this.cwNewLocationId && !this.cwIsDuplicate && !this.cwIsSaving;
  }

  addCompuweighEntry(): void {
    if (!this.cwCanAdd) return;
    this.cwIsSaving = true;
    const dto = {
      locDefaultId: 0,
      locCode: this.getEffectiveLocCode(),
      commodityCode: null,
      key: this.cwNewType,
      value: this.cwNewLocationId!.toString()
    };
    this._adminConfigService.saveTradeUnitDefault(dto).pipe(takeUntil(this._destroy$)).subscribe({
      next: () => {
        this.toastNotification.showToast('CompuWeigh site added', ACS_TOAST_SUCCESS_DURATION, 'success');
        this.cwIsSaving = false;
        this.resetCwForm();
        this.loadDefaults();
      },
      error: (err) => {
        const msg = extractErrorMessage(err, 'Failed to add CompuWeigh site');
        this.toastNotification.showToast(msg, ACS_TOAST_ERROR_DURATION, 'error');
        this.cwIsSaving = false;
      }
    });
  }

  removeCompuweighEntry(entry: CompuweighEntry): void {
    this._adminConfigService.deleteTradeUnitDefault(entry.locDefaultId).pipe(takeUntil(this._destroy$)).subscribe({
      next: () => {
        this.toastNotification.showToast('CompuWeigh site removed', ACS_TOAST_SUCCESS_DURATION, 'success');
        this.compuweighEntries = this.compuweighEntries.filter(e => e.locDefaultId !== entry.locDefaultId);
      },
      error: (err) => {
        const msg = extractErrorMessage(err, 'Failed to remove CompuWeigh site');
        this.toastNotification.showToast(msg, ACS_TOAST_ERROR_DURATION, 'error');
      }
    });
  }

  private resetCwForm(): void {
    this.cwNewType = '';
    this.cwNewLocationId = null;
    this.cwNewLocationDisplay = '';
    this.cwLocationResults = [];
    this.cwLocationSearchText.set('');
  }
}
